import { remove } from 'lodash';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { DynamicQueryObservable } from './dynamic-query-observable.model';

@Injectable({
  providedIn: 'root'
})
export class DynamicQueryStoreObservableFactory {
  /**
   *
   * @param decoratedObservable
   * @param comparer
   * @returns {Observable<T>|Observable<T>}
   */
  create<T>(decoratedObservable: DynamicQueryObservable<T>, comparer: (a: T, b: T) => boolean): Observable<T[]> {
    if (!(decoratedObservable.inserted && decoratedObservable.updated && decoratedObservable.deleted)) {
      throw new Error('Expected decorated observable');
    }

    if (typeof comparer !== 'function') {
      throw new Error('Expected comparer as a function');
    }

    return new Observable<T[]>(function (obs) {
      let storedItems = [];

      function inserted(item) {
        storedItems.push(item);
        obs.next(storedItems);
      }

      function updated(updatedItem) {
        remove(storedItems, function (item) {
          return comparer(updatedItem, item);
        });
        storedItems.push(updatedItem);
        obs.next(storedItems);
      }

      function deleted(removedItem) {
        remove(storedItems, function (item) {
          return comparer(removedItem, item);
        });
        obs.next(storedItems);
      }

      function done() {
        if (!storedItems.length) {
          obs.next(storedItems);
        }
      }

      function errorHandler(err) {
        obs.error(err);
      }

      const insertedDisposer = decoratedObservable.inserted().subscribe(inserted, errorHandler);
      const updatedDisposer = decoratedObservable.updated().subscribe(updated, errorHandler);
      const deletedDisposer = decoratedObservable.deleted().subscribe(deleted, errorHandler);
      const doneDisposer = decoratedObservable.done().subscribe(done, errorHandler);

      return function disposer() {
        insertedDisposer.unsubscribe();
        updatedDisposer.unsubscribe();
        deletedDisposer.unsubscribe();
        doneDisposer.unsubscribe();
      };
    });
  }
}
